import { create } from 'zustand';
import { supabase, type User, type Session } from '../lib/supabase';
import { loadProfile } from './profileLoad';
import { rememberLoginRefusal } from '@/pages/auth/loginFlow';

import { apiRequest } from '../lib/apiBase';
import { pushApi } from '../lib/api';
import { forgetPushDevice } from '../lib/pwa/push';
import { runSignedOutCleanup, forgetStoredSession } from '../lib/signOutCleanup';

let profileGeneration = 0;

/** How long a sign-out waits for Supabase before ending the session here alone. */
const SIGN_OUT_WAIT_MS = 4000;

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  let t: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, rej) => { t = setTimeout(() => rej(new Error('auth timeout')), ms); });
  return Promise.race([p.finally(() => clearTimeout(t)), timeout]);
}

/**
 * The person is no longer signed in — whichever way (the button, another tab,
 * an expired session). Nothing loaded for them may keep running or showing:
 * no profile load may still write, no notification feed, no realtime channel,
 * no cached screen, no permission set (lib/signOutCleanup.ts, 2026-10-01).
 */
function endSignedInState(): void {
  profileGeneration += 1;
  runSignedOutCleanup();
  void supabase.removeAllChannels().catch(() => {});
}

/**
 * GET /auth/me, and it gives up — a hung request must become a retry, not a
 * frozen screen. The site's own address gets 3.5 s of the 8, then the worker's
 * (lib/apiBase.ts — 2026-09-26, « Connexion au serveur impossible »).
 */
async function fetchMeWithTimeout(token: string, ms: number): Promise<Response> {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), ms);
  try {
    return await apiRequest('/auth/me', { headers: { Authorization: `Bearer ${token}` }, signal: ctl.signal }, { primaryTimeoutMs: 3500 });
  } finally {
    clearTimeout(t);
  }
}

interface Profile {
  id: string;
  full_name: string;
  phone: string | null;
  role: 'customer' | 'admin' | 'staff';
  /** Night mode, per person (migration 209): 'auto' | 'light' | 'dark'; null =
   *  never chosen; absent = this server has no such column yet. */
  theme_pref?: string | null;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  profileLoaded: boolean;
  /**
   * Set when the server could not be reached to say who this person is. The
   * screen then says so (with Retry) — it never guesses a role. See
   * store/profileLoad.ts for the 2026-09-25 « two gateways » incident.
   */
  profileError: string | null;
  init: () => Promise<void>;
  setSession: (session: Session | null) => void;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  profile: null,
  loading: true,
  profileLoaded: false,
  profileError: null,

  init: async () => {
    // supabase-js getSession() can DEADLOCK on iOS Safari (the Web Locks auth
    // lock never releases) and never resolve — which froze the ENTIRE app on the
    // boot spinner, because every route (incl. the PUBLIC customer flow) is gated
    // behind `loading`. Reported: order form → "stuck on loading" on iPhone.
    // Race each auth call against a timeout so `loading` ALWAYS resolves; the
    // public flow needs no auth, and onAuthStateChange re-hydrates a real session
    // if/when it arrives. Desktop resolves in ms, so no real delay there.
    try {
      const { data: { session } } = await withTimeout(supabase.auth.getSession(), 2500);
      set({ session, user: session?.user ?? null });

      if (session?.user) {
        // A bounded wait for the first answer. If the server is slow the load
        // carries on in the background and the guarded pages WAIT for it —
        // ⚠️ nothing here may claim the profile is known when it is not
        // (that claim is what sent an admin to the customer door, 25 Sept).
        await withTimeout(get().refreshProfile(), 2500).catch(() => {});
      }
    } catch (err) {
      console.error('Auth init error (continuing unauthenticated):', err);
    }
    // Nobody signed in: there is no profile to wait for.
    set({ loading: false, ...(get().user ? {} : { profileLoaded: true }) });

    supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (event === 'INITIAL_SESSION') return;
      set({ session: newSession, user: newSession?.user ?? null });
      if (event === 'SIGNED_OUT') {
        // The session ended WITHOUT our signOut() — expired, revoked, or a
        // logout in another tab. This device must stop getting alerts too: the
        // server can no longer be asked, but dropping the browser subscription
        // kills the endpoint (the server deletes its row on the next 404/410).
        // After our own signOut() this finds nothing left to do.
        void forgetPushDevice({
          serviceWorker: typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined,
          api: pushApi,
          timeoutMs: 3000,
        });
      }
      if (newSession?.user) {
        await get().refreshProfile();
      } else {
        // Signed out elsewhere (another tab) or the session expired.
        set({ profile: null, profileLoaded: true, profileError: null });
        endSignedInState();
      }
    });
  },

  setSession: (session) => {
    set({ session, user: session?.user ?? null });
  },

  signOut: async () => {
    // Every gate closes FIRST, before a network call that can hang (the iOS
    // auth lock) or fail (a phone hotspot's DNS): nothing outlives the click.
    set({ user: null, session: null, profile: null, profileLoaded: false, profileError: null });
    endSignedInState();

    // Then logging out stops push to this device (owner, 2026-10-01: « one
    // time I was logged out and I still got a notification »). BEFORE the
    // session ends, so it can still sign the DELETE — the token comes from
    // Supabase's stored session (lib/api.ts getAuthHeaders), not from the
    // store cleared above. Bounded and never throws, so it can never hold the
    // logout hostage (lib/pwa/push.ts).
    await forgetPushDevice({
      serviceWorker: typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined,
      api: pushApi,
      timeoutMs: 3000,
    });
    // Then end the session itself — on Supabase (its refresh token dies there)…
    let confirmed = false;
    try {
      const { error } = await withTimeout(supabase.auth.signOut(), SIGN_OUT_WAIT_MS);
      confirmed = !error;
    } catch { /* timed out, or the auth lock threw */ }
    // …and HERE. ⚠️ When Supabase could not be told, auth-js KEEPS the stored
    // session and only returns an error; the next tab focus then announced it
    // as SIGNED_IN and silently signed the admin back in — with notifications
    // over the login page (owner, 2026-10-01). Without it on disk, nothing can.
    if (!confirmed) forgetStoredSession();
  },

  refreshProfile: async () => {
    const { session } = get();
    if (!session?.access_token) {
      set({ profile: null, profileLoaded: true, profileError: null });
      return;
    }
    // Only the newest load may write — init and onAuthStateChange can overlap.
    const gen = ++profileGeneration;
    set({ profileError: null });

    // ⚠️ THE ROLE IS WHAT THE SERVER SAYS — retried, refreshed, or « unreachable »,
    // never a fabricated « customer » (store/profileLoad.ts).
    const out = await loadProfile({
      token: session.access_token,
      fetchMe: (token) => fetchMeWithTimeout(token, 8000),
      refresh: async () => {
        const { data, error } = await supabase.auth.refreshSession();
        if (error || !data.session) return null;
        set({ session: data.session, user: data.session.user });
        return data.session.access_token;
      },
      sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    });
    if (gen !== profileGeneration) return;

    if (out.kind === 'ok') {
      console.log('[auth] Profile loaded:', out.profile.role);
      set({ profile: out.profile as Profile, profileLoaded: true, profileError: null });
      return;
    }
    if (out.kind === 'signed_out') {
      console.warn('[auth] session rejected by the server — signing out', out.reason ?? '');
      // The login page says why (pages/auth/loginFlow.ts refusalMessage).
      if (out.reason) rememberLoginRefusal(out.reason);
      await get().signOut();
      return;
    }
    console.error('[auth] /auth/me unreachable after retries');
    set({ profile: null, profileLoaded: false, profileError: 'unreachable' });
  },
}));
